interface DateGroup<T> {
  name: string;
  items: T[];
}

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, days: number) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

const startOfWeek = (date: Date, weekStartsOn: number) => {
  const day = startOfDay(date);
  return addDays(day, -((day.getDay() - weekStartsOn + 7) % 7));
};

const weekdayFormat = new Intl.DateTimeFormat("en-US", { weekday: "long" });
const monthDayFormat = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "2-digit"
});
const fullDateFormat = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "2-digit"
});
const pastYearFormat = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "2-digit",
  year: "numeric"
});

interface DateBoundaries {
  today: number;
  yesterday: number;
  thisWeekStart: number;
  lastWeekStart: number;
  year: number;
}

const boundariesFor = (now: Date): DateBoundaries => ({
  today: startOfDay(now).getTime(),
  yesterday: addDays(startOfDay(now), -1).getTime(),
  thisWeekStart: startOfWeek(now, 1).getTime(),
  lastWeekStart: startOfWeek(addDays(now, -7), 1).getTime(),
  year: now.getFullYear()
});

const labelFor = (date: Date, boundaries: DateBoundaries) => {
  const day = startOfDay(date).getTime();

  if (day === boundaries.today) return "Today";
  if (day === boundaries.yesterday) return "Yesterday";

  if (day >= boundaries.thisWeekStart)
    return `${weekdayFormat.format(date)} - ${monthDayFormat.format(date)}`;

  if (day >= boundaries.lastWeekStart) return "Last Week";

  return date.getFullYear() === boundaries.year
    ? fullDateFormat.format(date)
    : pastYearFormat.format(date);
};

export const groupByDate = <T>(
  items: T[],
  dateOf: (item: T) => string | null | undefined,
  now: Date = new Date()
): DateGroup<T>[] => {
  const order: string[] = [];
  const boundaries = boundariesFor(now);

  const buckets = items.reduce((acc, item) => {
    const raw = dateOf(item);
    if (!raw) return acc;

    const name = labelFor(new Date(raw), boundaries);
    if (!acc.has(name)) {
      acc.set(name, []);
      order.push(name);
    }
    acc.get(name)?.push(item);

    return acc;
  }, new Map<string, T[]>());

  return order.map((name) => ({ name, items: buckets.get(name) ?? [] }));
};
